/**
 * particleSystem.ts
 * Pooled particle system for visual effects (enemy death bursts, collectible sparkles).
 *
 * AGENTS.md requires object pooling for particles with max 50 active objects.
 * Each particle has position, velocity, colour, lifetime, and alpha fade.
 * The system is updated once per physics tick and rendered by the platform renderer.
 *
 * Architecture: No React, no async. Pure engine module.
 *
 * @module particleSystem
 */

import { ObjectPool } from '../utils/objectPool';
import { MAX_PARTICLES } from '../../constants/gameConstants';
import { PARTICLE_GRAVITY } from './physics';

/** A single particle in the game world. */
export interface ParticleData {
  /** World-space X position. */
  x: number;
  /** World-space Y position. */
  y: number;
  /** Velocity X (px/s). */
  vx: number;
  /** Velocity Y (px/s). */
  vy: number;
  /** CSS colour string for rendering. */
  color: string;
  /** Remaining lifetime in seconds. */
  life: number;
  /** Starting lifetime (for alpha fade calculation). */
  maxLife: number;
  /** Particle size in px. */
  size: number;
  /** Whether this particle is currently active. */
  active: boolean;
}

/** Poolable particle wrapper (ObjectPool requires reset()). */
class PooledParticle implements ParticleData {
  x = 0;
  y = 0;
  vx = 0;
  vy = 0;
  color = '#ffffff';
  life = 0;
  maxLife = 0;
  size = 3;
  active = false;

  /** Reset to default state when returned to pool. */
  reset(): void {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.color = '#ffffff';
    this.life = 0;
    this.maxLife = 0;
    this.size = 3;
    this.active = false;
  }
}

// DECISION: Module-level pool + active array to avoid allocations in the hot path.
const pool = new ObjectPool<PooledParticle>(() => new PooledParticle(), MAX_PARTICLES);
pool.prewarm(MAX_PARTICLES);

/** All currently active particles — read by renderers. */
export const activeParticles: ParticleData[] = [];

/**
 * Spawn a burst of particles at the given position (e.g. enemy death).
 *
 * @param cx     - Centre X of the burst (world space).
 * @param cy     - Centre Y of the burst (world space).
 * @param color  - CSS colour for the particles.
 * @param count  - Number of particles to emit (capped by pool availability).
 */
export function emitBurst(cx: number, cy: number, color: string, count: number): void {
  for (let i = 0; i < count; i++) {
    const p = pool.acquire();
    if (!p) break; // pool exhausted — skip remaining
    // DECISION: Random radial spread with slight upward bias for a
    // visually pleasing pop. Speed 40–120 px/s, lifetime 0.3–0.5s.
    const angle = Math.random() * Math.PI * 2;
    const speed = 40 + Math.random() * 80;
    p.x = cx + (Math.random() - 0.5) * 8;
    p.y = cy + (Math.random() - 0.5) * 8;
    p.vx = Math.cos(angle) * speed;
    p.vy = Math.sin(angle) * speed - 30; // upward bias
    p.color = color;
    p.life = 0.3 + Math.random() * 0.2;
    p.maxLife = p.life;
    p.size = 2 + Math.random() * 2;
    p.active = true;
    activeParticles.push(p);
  }
}

/**
 * Update all active particles by one tick. Call once per physics step.
 * Dead particles are returned to the pool automatically.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateParticles(dt: number): void {
  for (let i = activeParticles.length - 1; i >= 0; i--) {
    const p = activeParticles[i] as PooledParticle;
    p.life -= dt;
    if (p.life <= 0) {
      p.active = false;
      activeParticles.splice(i, 1);
      pool.release(p);
      continue;
    }
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    // DECISION: Light gravity on particles for a natural arc — see
    // PARTICLE_GRAVITY in physics.ts for why it is far below world GRAVITY.
    p.vy += PARTICLE_GRAVITY * dt;
  }
}

/**
 * Clear all active particles and return them to the pool.
 * Call on scene teardown / restart.
 */
export function clearParticles(): void {
  for (let i = activeParticles.length - 1; i >= 0; i--) {
    pool.release(activeParticles[i] as PooledParticle);
  }
  activeParticles.length = 0;
}
